import { supabase } from '../lib/supabase';
import { auditLogger } from '../lib/auditLogger';
import { storage } from './storage';
import { BillingConfig, CrgLine, CrgStatement, DealStage, UserProfile } from '../types';

/**
 * Détermine si l'utilisateur voit le module Facturation.
 * - désactivé : personne
 * - activé sans bêta : tout le monde
 * - activé en bêta : admins/superadmins toujours ; utilisateurs locaux si une
 *   de leurs agences est dans la liste ; sinon selon l'agence sélectionnée
 */
export const canAccessBilling = (
  config: BillingConfig,
  userProfile: UserProfile | null,
  selectedAgencyId?: string | null,
  availableAgencies?: Array<{ id: string }>
): boolean => {
  if (!config.enabled || !userProfile) return false;
  if (userProfile.role === 'prestataire_marketing') return false;
  if (!config.beta) return true;
  if (userProfile.role === 'admin' || userProfile.role === 'superadmin') return true;
  if (userProfile.accessLevel === 'local') {
    return (availableAgencies || []).some(a => config.betaAgencyIds.includes(a.id));
  }
  return selectedAgencyId ? config.betaAgencyIds.includes(selectedAgencyId) : false;
};

/**
 * Service Facturation propriétaires (CRG) : lecture/écriture des crg_statements
 * (protégés par RLS agence) et résolution du taux de commission contractuel.
 * La génération depuis RentalReady passe par rentalReadyService.generateCrg.
 */

const mapStatement = (d: any): CrgStatement => ({
  id: d.id,
  agencyId: d.agency_id,
  propertyId: d.property_id,
  ownerId: d.owner_id,
  periodYear: d.period_year,
  periodMonth: d.period_month,
  status: d.status,
  rrRentalId: d.rr_rental_id,
  commissionRate: Number(d.commission_rate),
  rebillCleaning: !!d.rebill_cleaning,
  lines: (d.lines || []) as CrgLine[],
  headerSnapshot: d.header_snapshot || {},
  totalPaidOut: Number(d.total_paid_out),
  totalCleaning: Number(d.total_cleaning),
  totalNetRevenue: Number(d.total_net_revenue),
  totalCommission: Number(d.total_commission),
  totalBilled: Number(d.total_billed),
  currency: d.currency || 'EUR',
  pdfStoragePath: d.pdf_storage_path,
  generatedAt: d.generated_at,
  finalizedAt: d.finalized_at,
  finalizedBy: d.finalized_by,
});

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Recalcule les totaux d'un CRG à partir de ses lignes (lignes exclues ignorées). */
export const computeCrgTotals = (lines: CrgLine[], rebillCleaning: boolean) => {
  const active = lines.filter(l => !l.excluded);
  const totalPaidOut = round2(active.reduce((s, l) => s + (l.paidOut || 0), 0));
  const totalCleaning = round2(active.reduce((s, l) => s + (l.cleaningFee || 0), 0));
  const totalCityTax = round2(active.reduce((s, l) => s + (l.cityTax || 0), 0));
  const totalNetRevenue = round2(active.reduce((s, l) => s + (l.netRevenue || 0), 0));
  const totalCommission = round2(active.reduce((s, l) => s + (l.commissionAmount || 0), 0));
  const totalBilled = round2(totalCommission + (rebillCleaning ? totalCleaning : 0));
  return { totalPaidOut, totalCleaning, totalCityTax, totalNetRevenue, totalCommission, totalBilled };
};

export const billingService = {
  getCrgStatements: async (
    agencyId: string,
    filter?: { year?: number; month?: number; propertyId?: string }
  ): Promise<CrgStatement[]> => {
    let query = supabase
      .from('crg_statements')
      .select('*')
      .eq('agency_id', agencyId)
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false });

    if (filter?.year) query = query.eq('period_year', filter.year);
    if (filter?.month) query = query.eq('period_month', filter.month);
    if (filter?.propertyId) query = query.eq('property_id', filter.propertyId);

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching CRG statements:', error);
      return [];
    }
    return data?.map(mapStatement) || [];
  },

  getCrgStatement: async (id: string): Promise<CrgStatement | null> => {
    const { data, error } = await supabase.from('crg_statements').select('*').eq('id', id).maybeSingle();
    if (error) {
      console.error('Error fetching CRG statement:', error);
      return null;
    }
    return data ? mapStatement(data) : null;
  },

  /**
   * Met à jour les lignes d'un brouillon (exclusions, lignes manuelles) et
   * recalcule tous les totaux. Refusé sur un CRG finalisé.
   */
  updateDraftLines: async (id: string, lines: CrgLine[]): Promise<CrgStatement | null> => {
    const { data: existing, error: fetchError } = await supabase
      .from('crg_statements').select('*').eq('id', id).single();
    if (fetchError || !existing) {
      console.error('CRG statement not found:', fetchError);
      return null;
    }
    if (existing.status === 'FINALIZED') {
      throw new Error('CRG finalisé : modification interdite');
    }

    const totals = computeCrgTotals(lines, !!existing.rebill_cleaning);
    const update = {
      lines,
      total_paid_out: totals.totalPaidOut,
      total_cleaning: totals.totalCleaning,
      total_net_revenue: totals.totalNetRevenue,
      total_commission: totals.totalCommission,
      total_billed: totals.totalBilled,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('crg_statements').update(update).eq('id', id).select().single();
    if (error) {
      console.error('Error updating CRG draft:', error);
      throw new Error(error.message);
    }

    await auditLogger.log('UPDATE', 'crg_statements', id, existing, { ...existing, ...update });
    return mapStatement(data);
  },

  deleteDraft: async (id: string): Promise<void> => {
    const { data: existing } = await supabase.from('crg_statements').select('*').eq('id', id).single();
    if (!existing) return;
    if (existing.status === 'FINALIZED') {
      throw new Error('CRG finalisé : suppression interdite');
    }
    const { error } = await supabase.from('crg_statements').delete().eq('id', id);
    if (error) {
      console.error('Error deleting CRG draft:', error);
      throw new Error(error.message);
    }
    await auditLogger.log('DELETE', 'crg_statements', id, existing, null);
  },

  /**
   * Upload le PDF du CRG dans le bucket property-documents sous
   * {propertyId}/crg/{year}-{MM}_{timestamp}.pdf et retourne le chemin.
   */
  uploadCrgPdf: async (propertyId: string, year: number, month: number, blob: Blob): Promise<string> => {
    const fileName = `${propertyId}/crg/${year}-${String(month).padStart(2, '0')}_${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('property-documents')
      .upload(fileName, blob, { contentType: 'application/pdf' });
    if (error || !data) {
      console.error('Error uploading CRG pdf:', error);
      throw new Error(error?.message || 'Échec de l\'upload du PDF');
    }
    return data.path;
  },

  /**
   * Finalise un CRG : fige le document (statut FINALIZED + PDF archivé).
   * Le trigger DB empêche ensuite toute modification/suppression.
   */
  finalizeCrg: async (id: string, pdfPath: string): Promise<CrgStatement | null> => {
    const { data: existing, error: fetchError } = await supabase
      .from('crg_statements').select('*').eq('id', id).single();
    if (fetchError || !existing) {
      console.error('CRG statement not found:', fetchError);
      return null;
    }
    if (existing.status === 'FINALIZED') {
      throw new Error('CRG déjà finalisé');
    }

    const { data: session } = await supabase.auth.getSession();
    const update = {
      status: 'FINALIZED',
      pdf_storage_path: pdfPath,
      finalized_at: new Date().toISOString(),
      finalized_by: session?.session?.user?.id || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('crg_statements').update(update).eq('id', id).select().single();
    if (error) {
      console.error('Error finalizing CRG:', error);
      throw new Error(error.message);
    }

    await auditLogger.log('UPDATE', 'crg_statements', id, existing, { ...existing, ...update });
    return mapStatement(data);
  },

  getPdfUrl: async (path: string): Promise<string | null> => {
    return storage.getFileUrl(path);
  },

  /**
   * Taux de commission hérité du contrat signé du bien
   * (chemin property -> deals -> contracts, pas de FK directe).
   */
  getContractCommissionRate: async (propertyId: string): Promise<number | null> => {
    const { data: dealRows, error } = await supabase
      .from('deals')
      .select('id, contract_id, stage, created_at')
      .eq('property_id', propertyId)
      .not('contract_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error || !dealRows?.length) return null;

    // Priorité au deal signé le plus récent, sinon au plus récent avec contrat
    const signedFirst = [...dealRows].sort((a, b) => {
      const aSigned = a.stage === DealStage.SIGNED ? 0 : 1;
      const bSigned = b.stage === DealStage.SIGNED ? 0 : 1;
      return aSigned - bSigned;
    });

    for (const deal of signedFirst) {
      const { data: contract } = await supabase
        .from('contracts')
        .select('commission_rate')
        .eq('id', deal.contract_id)
        .maybeSingle();
      if (contract?.commission_rate != null) return Number(contract.commission_rate);
    }
    return null;
  },
};
